import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Legal Notice page - Required by French law (LCEN Article 6-III)
 */

const PageContainer = styled.main`
  max-width: 900px;
  margin: 120px auto 80px;
  padding: 0 ${props => props.theme.spacing[6]};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    margin: 100px auto 60px;
    padding: 0 ${props => props.theme.spacing[4]};
  }
`;

const PageTitle = styled.h1`
  font-size: ${props => props.theme.typography.sizes['4xl']};
  font-weight: ${props => props.theme.typography.weights.bold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing[8]};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.sizes['3xl']};
  }
`;

const Section = styled.section`
  margin-bottom: ${props => props.theme.spacing[10]};
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.typography.sizes['2xl']};
  font-weight: ${props => props.theme.typography.weights.semibold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing[4]};
  padding-bottom: ${props => props.theme.spacing[2]};
  border-bottom: 2px solid ${props => props.theme.colors.primary[500]};
`;

const Paragraph = styled.p`
  font-size: ${props => props.theme.typography.sizes.base};
  line-height: ${props => props.theme.typography.lineHeights.relaxed};
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: ${props => props.theme.spacing[4]};
  
  strong {
    color: ${props => props.theme.colors.text};
    font-weight: ${props => props.theme.typography.weights.semibold};
  }
`;

const InfoBlock = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-left: 4px solid ${props => props.theme.colors.primary[500]};
  padding: ${props => props.theme.spacing[4]};
  border-radius: ${props => props.theme.borderRadius.lg};
  margin-bottom: ${props => props.theme.spacing[4]};
`;

const Link = styled.a`
  color: ${props => props.theme.colors.primary[600]};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const LegalNotice: React.FC = () => {
  const { language } = useLanguage();

  return (
    <PageContainer>
      <PageTitle>
        {language === 'fr' ? 'Mentions Légales' : 'Legal Notice'}
      </PageTitle>

      <Section>
        <SectionTitle>
          {language === 'fr' ? '1. Éditeur du site' : '1. Site Publisher'}
        </SectionTitle>
        <InfoBlock>
          <Paragraph>
            <strong>{language === 'fr' ? 'Nom :' : 'Name:'}</strong> Nadir Benadda
          </Paragraph>
          <Paragraph>
            <strong>{language === 'fr' ? 'Statut :' : 'Status:'}</strong>{' '}
            {language === 'fr' 
              ? 'Particulier - Portfolio personnel non commercial' 
              : 'Individual - Non-commercial personal portfolio'}
          </Paragraph>
          <Paragraph>
            <strong>{language === 'fr' ? 'Contact :' : 'Contact:'}</strong>{' '}
            <Link href="mailto:n.benadda.dev@gmail.com">n.benadda.dev@gmail.com</Link>
          </Paragraph>
          <Paragraph>
            <strong>{language === 'fr' ? 'Directeur de publication :' : 'Publication Director:'}</strong> Nadir Benadda
          </Paragraph>
        </InfoBlock>
      </Section>

      <Section>
        <SectionTitle>
          {language === 'fr' ? '2. Hébergeur du site' : '2. Web Hosting'}
        </SectionTitle>
        <InfoBlock>
          <Paragraph>
            <strong>{language === 'fr' ? 'Nom :' : 'Name:'}</strong> Vercel Inc.
          </Paragraph>
          <Paragraph>
            <strong>{language === 'fr' ? 'Adresse :' : 'Address:'}</strong> 340 S Lemon Ave #4133, Walnut, CA 91789, USA
          </Paragraph>
          <Paragraph>
            <strong>{language === 'fr' ? 'Site web :' : 'Website:'}</strong>{' '}
            <Link href="https://vercel.com" target="_blank" rel="noopener noreferrer">
              vercel.com
            </Link>
          </Paragraph>
        </InfoBlock>
      </Section>

      <Section>
        <SectionTitle>
          {language === 'fr' ? '3. Propriété intellectuelle' : '3. Intellectual Property'}
        </SectionTitle>
        <Paragraph>
          {language === 'fr'
            ? "L'ensemble du contenu présent sur ce site (textes, images, code source, design) est la propriété exclusive de Nadir Benadda, sauf mention contraire."
            : "All content on this site (texts, images, source code, design) is the exclusive property of Nadir Benadda, unless otherwise stated."}
        </Paragraph>
        <Paragraph>
          {language === 'fr'
            ? "Toute reproduction, distribution, modification, adaptation, retransmission ou publication de ces différents éléments est strictement interdite sans l'accord écrit de l'auteur."
            : "Any reproduction, distribution, modification, adaptation, retransmission or publication of these elements is strictly prohibited without the written consent of the author."}
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>
          {language === 'fr' ? '4. Données personnelles' : '4. Personal Data'}
        </SectionTitle>
        <Paragraph>
          {language === 'fr'
            ? "Pour plus d'informations sur la collecte et le traitement de vos données personnelles, veuillez consulter notre "
            : "For more information on the collection and processing of your personal data, please consult our "}
          <Link href="/politique-confidentialite">
            {language === 'fr' ? 'Politique de Confidentialité' : 'Privacy Policy'}
          </Link>.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>
          {language === 'fr' ? '5. Cookies' : '5. Cookies'}
        </SectionTitle>
        <Paragraph>
          {language === 'fr'
            ? "Ce site n'utilise actuellement aucun cookie de suivi ou d'analyse. Si cela venait à changer, vous en seriez informé et votre consentement serait demandé conformément à la réglementation en vigueur."
            : "This site currently does not use any tracking or analytics cookies. If this changes, you will be informed and your consent will be requested in accordance with current regulations."}
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>
          {language === 'fr' ? '6. Limitation de responsabilité' : '6. Limitation of Liability'}
        </SectionTitle>
        <Paragraph>
          {language === 'fr'
            ? "Nadir Benadda ne pourra être tenu responsable des dommages directs ou indirects causés au matériel de l'utilisateur lors de l'accès au site."
            : "Nadir Benadda cannot be held responsible for direct or indirect damage to the user's equipment when accessing the site."}
        </Paragraph>
        <Paragraph>
          {language === 'fr'
            ? "Les informations fournies sur ce site le sont à titre indicatif. L'éditeur du site se réserve le droit de modifier ces informations à tout moment."
            : "The information provided on this site is for informational purposes only. The site publisher reserves the right to modify this information at any time."}
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>
          {language === 'fr' ? '7. Liens hypertextes' : '7. Hyperlinks'}
        </SectionTitle>
        <Paragraph>
          {language === 'fr'
            ? "Ce site peut contenir des liens vers des sites externes. Nadir Benadda n'exerce aucun contrôle sur ces sites et décline toute responsabilité quant à leur contenu."
            : "This site may contain links to external sites. Nadir Benadda exercises no control over these sites and disclaims all responsibility for their content."}
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>
          {language === 'fr' ? '8. Droit applicable' : '8. Applicable Law'}
        </SectionTitle>
        <Paragraph>
          {language === 'fr'
            ? "Les présentes mentions légales sont régies par le droit français. En cas de litige, les tribunaux français seront seuls compétents."
            : "These legal notices are governed by French law. In case of dispute, French courts shall have sole jurisdiction."}
        </Paragraph>
      </Section>

      <Paragraph style={{ marginTop: '60px', textAlign: 'center', fontSize: '0.875rem' }}>
        {language === 'fr' 
          ? 'Dernière mise à jour : 1er octobre 2025' 
          : 'Last updated: October 1, 2025'}
      </Paragraph>
    </PageContainer>
  );
};

export default LegalNotice;
